/**
 * Hooks de TanStack Query para el módulo de Cierre de Cuentas.
 *
 * @taskId T464, T466
 * @see functional/Specs/specs_cycle_4.md HU-15-FE, HU-16-FE
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  closeAccountInvoluntary,
  ClosureApiError,
  getClosureStatus,
} from '../../../api/closure.api';
import type {
  CloseAccountInvoluntaryPayload,
  CloseAccountResponse,
  ClosureStatusResponse,
} from '../types/closure.types';

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const closureKeys = {
  all: ['closure'] as const,
  status: (accountId: string) => [...closureKeys.all, 'status', accountId] as const,
};

// ─── Query Hooks ────────────────────────────────────────────────────────────

interface UseClosureStatusOptions {
  /** Habilitar polling automático */
  enablePolling?: boolean;
  /** Intervalo de polling en ms (default: 10000) */
  pollingInterval?: number;
}

/**
 * Hook para consultar el estado de cierre de una cuenta.
 * Soporta polling automático configurable.
 *
 * @param accountId ID de la cuenta
 * @param options Opciones de polling
 * @returns Query result con ClosureStatusResponse | null
 *
 * @rule RULE-11 — 404 no es error, retorna null
 * @see HU-16-FE, HU-51-BE
 */
export function useClosureStatus(
  accountId: string | undefined,
  options: UseClosureStatusOptions = {},
) {
  const { enablePolling = false, pollingInterval = 10_000 } = options;

  return useQuery<ClosureStatusResponse | null, ClosureApiError>({
    queryKey: closureKeys.status(accountId ?? ''),
    queryFn: () => getClosureStatus(accountId!),
    enabled: Boolean(accountId),
    staleTime: 5_000, // 5 segundos
    refetchInterval: enablePolling ? pollingInterval : false,
    // No refetch en ventanas ocultas durante polling
    refetchIntervalInBackground: false,
  });
}

/**
 * Hook con polling manual controlable.
 * Útil cuando el usuario quiere pausar/reanudar actualizaciones.
 */
export function useClosureStatusWithPolling(accountId: string | undefined) {
  const queryClient = useQueryClient();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const query = useClosureStatus(accountId, { enablePolling: false });

  const startPolling = useCallback(
    (intervalMs = 10_000) => {
      if (intervalRef.current) return; // Ya está activo

      intervalRef.current = setInterval(() => {
        if (accountId) {
          queryClient.invalidateQueries({ queryKey: closureKeys.status(accountId) });
        }
      }, intervalMs);
      setIsPolling(true);
    },
    [accountId, queryClient],
  );

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setIsPolling(false);
    }
  }, []);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    ...query,
    startPolling,
    stopPolling,
    isPolling,
  };
}

// ─── Mutation Hooks ─────────────────────────────────────────────────────────

interface CloseAccountVariables {
  accountId: string;
  payload: CloseAccountInvoluntaryPayload;
}

/**
 * Hook para iniciar cierre involuntario de una cuenta.
 * Invalida queries relacionadas tras éxito.
 *
 * @rule RULE-29 — Solo PSP_ADMIN puede usar
 * @rule RULE-16 — Motivo obligatorio
 * @see HU-15-FE, HU-50-BE
 */
export function useCloseAccountInvoluntary() {
  const queryClient = useQueryClient();

  return useMutation<CloseAccountResponse, ClosureApiError, CloseAccountVariables>({
    mutationFn: ({ accountId, payload }) => closeAccountInvoluntary(accountId, payload),
    onSuccess: (_data, variables) => {
      // Invalidar estado de cierre para que se refetch
      queryClient.invalidateQueries({
        queryKey: closureKeys.status(variables.accountId),
      });
      // Invalidar queries de cuentas
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}
